import type { CollectionReference, Firestore } from 'firebase/firestore'
import {
  collection,
  doc,
  query,
  serverTimestamp,
  where,
  writeBatch,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { type FirestoreOrganizationSectionPassCoupon, schema } from './schema'
import { getDocsWithError } from '../../firestore-mobx/fetch'
import { convertDocumentSnapshotToModel } from '../../firestore-mobx/stream'
import { OrganizationSectionPassCoupon } from '../../models/OrganizationSectionPassCoupon'
import { type FirebaseRepository } from '../../models/FirebaseRepository'

const converter: FirestoreDataConverter<FirestoreOrganizationSectionPassCoupon> =
  {
    toFirestore: (data) => {
      return data
    },
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return schema.parse(data)
    },
  }

export const getOrganizationSectionPassCouponColRef = (
  firestore: Firestore,
  organizationId: string,
  folderId: string
): CollectionReference<FirestoreOrganizationSectionPassCoupon> => {
  return collection(
    firestore,
    'organization',
    organizationId,
    'section_pass_coupon_folder',
    folderId,
    'section_pass_coupon'
  ).withConverter(converter)
}

export const getOrganizationSectionPassCouponDocRef = (
  firestore: Firestore,
  organizationId: string,
  folderId: string,
  couponId: string
) => {
  return doc(
    getOrganizationSectionPassCouponColRef(firestore, organizationId, folderId),
    couponId
  )
}

export const fetchOrganizationSectionPassCoupons = async ({
  repository,
  organizationId,
  folderId,
  consumed,
}: {
  repository: FirebaseRepository
  organizationId: string
  folderId: string
  consumed: boolean
}) => {
  const couponsColRef = getOrganizationSectionPassCouponColRef(
    repository.firestore,
    organizationId,
    folderId
  ).withConverter(converter)

  const q = query(couponsColRef, where('consumed', '==', consumed))

  const snapshot = await getDocsWithError(
    q,
    'FetchOrganizationSectionPassCouponsError'
  )

  return snapshot.docs.map((doc) =>
    convertDocumentSnapshotToModel(
      repository,
      doc,
      OrganizationSectionPassCoupon
    )
  )
}

/**
 * Creates a new folder with N number of coupons, using collection group queries
 * uses GUID format for coupon codes.
 *
 * NOTE: this is supported in all modern browsers except IE and some mobile browsers.
 * lets hope corre is not trying to manage org coupons from opera mini :)
 */
export async function createFolderWithCoupons({
  firestore,
  organizationId,
  folderName,
  couponCount,
  expiresAt: _expiresAt = null,
}: {
  firestore: Firestore
  organizationId: string
  folderName: string
  couponCount: number
  expiresAt?: Date | null
}) {
  if (couponCount <= 0) throw new Error('Coupon count must be greater than 0')

  // create folder and coupons
  const folderColRef = collection(
    firestore,
    'organization',
    organizationId,
    'section_pass_coupon_folder'
  )

  const folderDocRef = doc(folderColRef)

  const couponColRef = getOrganizationSectionPassCouponColRef(
    firestore,
    organizationId,
    folderDocRef.id
  )

  // create folder and all coupons in one batch
  const batch = writeBatch(firestore)

  const expiresAt = _expiresAt ? _expiresAt : new Date()

  // expiration should be 6 months from now unless defined
  if (!_expiresAt) expiresAt.setMonth(expiresAt.getMonth() + 6)

  const allValidCodes = new Array(couponCount)
    .fill(null)
    .reduce<Array<string>>((acc) => {
      const code = crypto.randomUUID().toUpperCase()
      acc.push(code)
      return acc
    }, [])

  // folder
  batch.set(folderDocRef, {
    organizationId,
    folderName,
    updatedAt: serverTimestamp(),
    expiresAt,
    show: true,
  })

  // coupons
  allValidCodes.forEach((code) => {
    const couponDocRef = doc(couponColRef, code)
    batch.set(couponDocRef, {
      consumed: false,
      organizationId,
      sectionPassCouponFolderId: folderDocRef.id,
      sectionPassCouponId: code,
      updatedAt: serverTimestamp(),
    })
  })

  // commit the batch
  await batch.commit()

  return {
    folderId: folderDocRef.id,
    couponIds: Array.from(allValidCodes),
  }
}
